// Stage 200: pure Shape-Q builder.
//
// Takes a settled turn snapshot + the accepted bite + the conversation
// context, returns a fully-populated LaughterEngramInput ready to POST.
// No side effects, no I/O. Test target is mostly here.

import type { Bite, BiteKind, SwatEntry } from '../../api/gadfly';
import { finalTagOf } from './finalTag';
import type {
  AcceptedBite,
  LaughterCorrelations,
  LaughterEngramInput,
  LaughterSubclass,
  LaughterSwat,
  RejectedSibling,
} from './types';

const SUBCLASS_BY_KIND: Record<BiteKind, LaughterSubclass> = {
  OBSERVATION: 'omission-laugh',
  QUESTION: 'ambiguity-laugh',
  OBJECTION: 'contradiction-laugh',
};

export function buildEngram(args: {
  acceptedBite: Bite;
  acceptedSwat: SwatEntry; // the ACCEPT swat for this bite
  allBites: ReadonlyArray<Bite>;
  allSwats: ReadonlyArray<SwatEntry>;
  userMessage: string;
  personaResponse: string;
  turnId: string;
  conversationId: string;
  firedAt: string; // ISO-8601 from the subscriber
  /** Stage 201-P: optional cross-pillar correlation snapshot. When
   *  undefined (the flag was off), the field is OMITTED from the engram
   *  entirely; when present (even with both sub-fields null), it is kept
   *  — the undefined-vs-null distinction is load-bearing for honest
   *  historical accounting. */
  correlations?: LaughterCorrelations;
}): LaughterEngramInput {
  const {
    acceptedBite,
    acceptedSwat,
    allBites,
    allSwats,
    userMessage,
    personaResponse,
    turnId,
    conversationId,
    firedAt,
    correlations,
  } = args;

  if (acceptedSwat.biteIndex !== acceptedBite.index) {
    throw new Error(
      'buildEngram: acceptedSwat.biteIndex must equal acceptedBite.index',
    );
  }
  if (acceptedSwat.tag !== 'ACCEPT') {
    throw new Error('buildEngram: acceptedSwat.tag must be ACCEPT');
  }

  const subclass = SUBCLASS_BY_KIND[acceptedBite.kind];

  const accepted: AcceptedBite = {
    index: acceptedBite.index,
    kind: acceptedBite.kind,
    text: acceptedBite.text,
    rawLine: acceptedBite.rawLine,
  };

  const swat: LaughterSwat = {
    biteIndex: acceptedSwat.biteIndex,
    tag: 'ACCEPT',
    reason: acceptedSwat.reason ?? '',
    taggedAt: acceptedSwat.taggedAt,
  };

  // rejectedSiblings: every OTHER bite in the turn with its final tag.
  // Co-accepted bites appear with finalTag='ACCEPT' (TINS §Field notes).
  const rejectedSiblings: RejectedSibling[] = [];
  for (const b of allBites) {
    if (b.index === acceptedBite.index) continue;
    const ft = finalTagOf(b, allSwats);
    const swatEntry = allSwats.find((e) => e.biteIndex === b.index) ?? null;
    rejectedSiblings.push({
      index: b.index,
      kind: b.kind,
      text: b.text,
      finalTag: ft,
      reason: swatEntry?.reason ?? null,
    });
  }

  return {
    subclass,
    acceptedBite: accepted,
    swat,
    rejectedSiblings,
    userMessage,
    personaResponse,
    turnId,
    conversationId,
    firedAt,
    ...(correlations !== undefined ? { correlations } : {}),
  };
}
